/**
 * GMA-1620 (CompassCare Web 5/14 — Clients) — pure clients model.
 *
 * UI-free logic the Clients management page renders: the tab set (All / Group
 * Homes / Independent Living), the tab filter, the risk-priority sort (worst
 * severity first, then online-before-offline), and the group-home rollup — the
 * AC's "group-home view rolls clients up by home with per-home status".
 *
 * Severity vocabulary + urgency come from the shared overviewModel so ranking
 * stays in lockstep across screens. Fail-safe like the rest of the facelift:
 * non-array inputs and malformed/primitive rows never throw.
 */
import { severityUrgency } from "./overviewModel";

// Client management tabs. `all` is the default; `homes`/`independent` map to a
// livingEnv. Keys are stable; labels are the human-facing tab text.
export const CLIENT_TABS = Object.freeze([
  { key: "all", label: "All Clients" },
  { key: "homes", label: "Group Homes" },
  { key: "independent", label: "Independent Living" },
]);

// Tab -> livingEnv it scopes to. `all` (and any unknown tab) is not listed and
// falls open to the full roster.
const TAB_ENV = Object.freeze({ homes: "group_home", independent: "independent" });

const objectRows = (clients) =>
  Array.isArray(clients)
    ? clients.filter((c) => c !== null && typeof c === "object" && !Array.isArray(c))
    : [];

/**
 * Filter the roster by tab. `all` / unknown tab -> everyone (fail-open, never
 * blanks the list); env tabs filter on `livingEnv`.
 * @param {Array} clients
 * @param {string} [tab="all"]
 * @returns {Array}
 */
export function filterClientsByTab(clients, tab = "all") {
  const list = objectRows(clients);
  const env = Object.prototype.hasOwnProperty.call(TAB_ENV, tab) ? TAB_ENV[tab] : null;
  if (!env) return list;
  return list.filter((c) => c.livingEnv === env);
}

/**
 * Order clients by triage priority: worst clinical severity first (via the
 * shared severityUrgency), then online before offline. Non-mutating; tolerates
 * a non-array input and primitive rows.
 * @param {Array} clients
 * @returns {Array} a new, sorted array
 */
export function sortClientsByRisk(clients) {
  return objectRows(clients)
    .slice()
    .sort((a, b) => {
      const ua = severityUrgency(a.status);
      const ub = severityUrgency(b.status);
      if (ua !== ub) return ub - ua; // higher urgency first
      // Same severity: online (not === false) before offline.
      const oa = a.online === false ? 1 : 0;
      const ob = b.online === false ? 1 : 0;
      return oa - ob;
    });
}

/** Normalized display name for a group-home client ("Unknown home" when blank
 * or whitespace-only — Copilot #307: "   " must group with the other unnamed
 * homes, not split into its own group). Trimmed so " Cedar House " and
 * "Cedar House" roll up together. */
export function homeDisplayName(client) {
  const name =
    client && typeof client.home === "string" ? client.home.trim() : "";
  return name || "Unknown home";
}

/**
 * Stable grouping key for a group-home client: by homeId when present (any
 * non-nullish value), else by normalized home name. Shared so every surface
 * (roster roll-up, INTEL impact counts, …) groups homes identically.
 * @param {object} client
 * @returns {string}
 */
export function homeKey(client) {
  const hasHomeId =
    client && client.homeId !== null && client.homeId !== undefined;
  return hasHomeId ? `id:${client.homeId}` : `name:${homeDisplayName(client)}`;
}

/**
 * Roll group-home clients up by home: one entry per home with its clients, a
 * per-home worst status (highest-urgency status among its residents), and a
 * count. Independent-living clients are excluded. Homes are ordered worst-status
 * first, then by home name. Fail-safe on non-array input / missing home fields.
 * @param {Array} clients
 * @returns {Array<{homeId: *, home: string, clients: Array, status: string, count: number}>}
 */
export function rollUpByHome(clients) {
  const byHome = new Map();
  for (const c of objectRows(clients)) {
    if (c.livingEnv !== "group_home") continue;
    // Key on homeId when present, else on the NORMALIZED display name, so
    // id-less rows that render as the same home (e.g. home:"" and
    // home:undefined both -> "Unknown home") group together instead of
    // splitting. (For id-less rows the normalization runs again when storing
    // `home` — harmless, it is a pure function.)
    const hasHomeId = c.homeId !== null && c.homeId !== undefined;
    const key = homeKey(c);
    if (!byHome.has(key)) {
      byHome.set(key, {
        homeId: hasHomeId ? c.homeId : null,
        home: homeDisplayName(c),
        clients: [],
      });
    }
    byHome.get(key).clients.push(c);
  }

  const homes = [...byHome.values()].map((h) => {
    // Worst status = the highest-urgency status among residents.
    let worst = "normal";
    let worstUrgency = -Infinity;
    for (const c of h.clients) {
      const u = severityUrgency(c.status);
      if (u > worstUrgency) {
        worstUrgency = u;
        worst = c.status;
      }
    }
    return { ...h, status: worst, count: h.clients.length };
  });

  return homes.sort(
    (a, b) =>
      severityUrgency(b.status) - severityUrgency(a.status) ||
      String(a.home).localeCompare(String(b.home)),
  );
}
